import { isEmptyBackreference, isLengthRangeMinZero, isStrictBackreference, isZeroLength, } from "./basic";
import { CacheInstance } from "./cache";
import { FirstConsumedChars, getFirstCharAfter, getFirstConsumedChar, getFirstConsumedCharAfter, } from "./next-char";
import { toUnicodeSet } from "./to-char-set";
import { assertNever } from "./util";
/**
 * Returns the longest knowable prefix guaranteed to always be accepted by the
 * given alternative (ignoring assertions).
 *
 * All character sets except the last one are guaranteed to be non-empty. The
 * last character set is only guaranteed to be non-empty if `includeAfter: false`.
 */
export function getLongestPrefix(alternative, direction, flags, options = {}) {
    const cacheInstance = CacheInstance.from(flags);
    flags = cacheInstance;
    const { includeAfter = false, onlyInside = false, looseGroups = false } = options;
    const cache = cacheInstance.getLongestPrefix;
    const cacheKey = `${direction},${includeAfter},${onlyInside},${looseGroups}`;
    let weakCache = cache.get(cacheKey);
    if (weakCache === undefined) {
        weakCache = new WeakMap();
        cache.set(cacheKey, weakCache);
    }
    let cached = weakCache.get(alternative);
    if (cached === undefined) {
        cached = getLongestPrefixImpl(alternative, direction, { includeAfter, onlyInside, looseGroups, root: alternative }, flags);
        weakCache.set(alternative, cached);
    }
    return cached;
}
function getLongestPrefixImpl(alternative, direction, options, flags) {
    const { chars, complete } = getAlternativePrefix(alternative, direction, options, flags);
    // try to find empty character sets
    for (let i = 0; i < chars.length; i++) {
        if (chars[i].isEmpty) {
            return chars.slice(0, i);
        }
    }
    // append the next character after the alternative
    if (complete && options.includeAfter && !options.onlyInside) {
        return [...chars, getFirstCharAfterAlternative(alternative, direction, flags).char];
    }
    return chars;
}
const EMPTY_COMPLETE = { chars: [], complete: true };
const EMPTY_INCOMPLETE = { chars: [], complete: false };
function getAlternativePrefix(alternative, direction, options, flags) {
    const { elements } = alternative;
    const chars = [];
    const first = direction === "ltr" ? 0 : elements.length - 1;
    const inc = direction === "ltr" ? +1 : -1;
    for (let i = first; i >= 0 && i < elements.length; i += inc) {
        const inner = getElementPrefix(elements[i], direction, options, flags);
        chars.push(...inner.chars);
        if (!inner.complete) {
            return { chars, complete: false };
        }
    }
    return { chars, complete: true };
}
function getElementPrefix(element, direction, options, flags) {
    switch (element.type) {
        case "Assertion":
            return EMPTY_COMPLETE;
        case "Character":
        case "CharacterClass":
        case "CharacterSet":
        case "ExpressionCharacterClass": {
            const set = toUnicodeSet(element, flags);
            if (set.accept.isEmpty) {
                return { chars: [set.chars], complete: true };
            }
            else {
                const prefixes = [];
                if (!set.chars.isEmpty) {
                    prefixes.push({ chars: [set.chars], complete: true });
                }
                for (const wordSet of set.accept.wordSets) {
                    prefixes.push({ chars: wordSet, complete: true });
                }
                return getAlternationPrefix(element, prefixes, direction, options, flags);
            }
        }
        case "CapturingGroup":
        case "Group":
            return getGroupPrefix(element, direction, options, flags);
        case "Quantifier":
            return getQuantifierPrefix(element, direction, options, flags);
        case "Backreference": {
            if (isEmptyBackreference(element, flags)) {
                return EMPTY_COMPLETE;
            }
            if (isStrictBackreference(element)) {
                const inner = getElementPrefix(element.resolved, direction, Object.assign(Object.assign({}, options), { includeAfter: false }), flags);
                return inner;
            }
            if (!mayLookAhead(element, options, direction, flags)) {
                return EMPTY_INCOMPLETE;
            }
            const look = FirstConsumedChars.toLook(getFirstConsumedCharPlusAfter(element, direction, flags));
            return { chars: [look.char], complete: false };
        }
        default:
            assertNever(element);
    }
}
function getGroupPrefix(element, direction, options, flags) {
    const alternatives = element.alternatives.map(a => getAlternativePrefix(a, direction, options, flags));
    return getAlternationPrefix(element, alternatives, direction, options, flags);
}
function getAlternationPrefix(element, alternatives, direction, options, flags) {
    if (alternatives.length === 1) {
        return alternatives[0];
    }
    const chars = [];
    let complete = true;
    /** Counts the number of different characters in strict mode */
    let differentCount = 0;
    for (let i = 0; complete; i++) {
        const cs = [];
        let end = false;
        for (const a of alternatives) {
            if (i >= a.chars.length) {
                end = true;
            }
            else {
                cs.push(a.chars[i]);
                if (i === a.chars.length - 1 && !a.complete && options.includeAfter) {
                    complete = false;
                }
            }
        }
        if (cs.length === 0) {
            // This means that all alternatives are complete and have the same
            // length, so we can stop here.
            break;
        }
        if (end) {
            // This means that one (but not all) complete alternatives have
            // reached the end, so we have consider the chars after the group.
            complete = false;
            if (!mayLookAheadAfter(element, options, direction, flags)) {
                break;
            }
            cs.push(getFirstCharAfter(element, direction, flags).char);
        }
        else if (!options.looseGroups) {
            if (complete && cs.some(c => !c.equals(cs[0]))) {
                differentCount++;
            }
            if (differentCount >= 2) {
                complete = false;
                if (!options.includeAfter) {
                    break;
                }
            }
        }
        const total = cs[0].union(...cs.slice(1));
        chars.push(total);
    }
    return { chars, complete };
}
function getQuantifierPrefix(element, direction, options, flags) {
    if (isZeroLength(element, flags)) {
        return EMPTY_COMPLETE;
    }
    if (isLengthRangeMinZero(element, flags)) {
        if (!mayLookAhead(element, options, direction, flags)) {
            return EMPTY_INCOMPLETE;
        }
        const look = FirstConsumedChars.toLook(getFirstConsumedCharPlusAfter(element, direction, flags));
        return { chars: [look.char], complete: false };
    }
    const inner = getElementPrefix(element.element, direction, options, flags);
    if (!inner.complete) {
        return inner;
    }
    if (inner.chars.length === 0) {
        // The quantifier is not of length zero and the inner element is complete.
        // If the algorithm is implemented correctly, `inner` will be at least on character long.
        throw new Error(`Expected the quantifier '${element.raw}' to consume at least one character.`);
    }
    const chars = [];
    for (let i = 0; i < element.min; i++) {
        chars.push(...inner.chars);
        if (chars.length > 1000) {
            // this is a safe-guard to protect against regexes like a{1000000}
            return { chars, complete: false };
        }
    }
    if (element.min === element.max) {
        return { chars, complete: true };
    }
    if (mayLookAheadAfter(element, options, direction, flags)) {
        const look = getFirstCharAfter(element, direction, flags);
        chars.push(look.char.union(inner.chars[0]));
    }
    return { chars, complete: false };
}
/**
 * This operations is equal to:
 *
 * ```
 * concat(
 *     getFirstConsumedChar(element, direction, flags),
 *     getFirstConsumedCharAfter(element, direction, flags),
 * )
 * ```
 */
function getFirstConsumedCharPlusAfter(element, direction, flags) {
    const consumed = getFirstConsumedChar(element, direction, flags);
    if (!consumed.empty) {
        return consumed;
    }
    return FirstConsumedChars.concat([consumed, getFirstConsumedCharAfter(element, direction, flags)], flags);
}
function getFirstCharAfterAlternative(alternative, direction, flags) {
    const { elements } = alternative;
    const last = direction === "rtl" ? 0 : elements.length - 1;
    const inc = direction === "ltr" ? +1 : -1;
    // The idea here is to go back as far as possible into the alternative without consuming a characters.
    // This allows assertions inside the alternative to affect the character after it.
    let afterThis = last;
    while (afterThis >= 0 && afterThis < elements.length && isZeroLength(elements[afterThis], flags)) {
        afterThis -= inc;
    }
    if (afterThis >= 0 && afterThis < elements.length) {
        return getFirstCharAfter(elements[afterThis], direction, flags);
    }
    else {
        return FirstConsumedChars.toLook(getFirstConsumedCharPlusAfter(alternative, direction, flags));
    }
}
function mayLookAhead(element, options, direction, flags) {
    if (!options.includeAfter) {
        return false;
    }
    if (options.onlyInside) {
        return isNextCharacterInside(element, direction, options.root, flags);
    }
    return true;
}
function mayLookAheadAfter(element, options, direction, flags) {
    if (!options.includeAfter) {
        return false;
    }
    if (options.onlyInside) {
        return isNextCharacterInsideAfter(element, direction, options.root, flags);
    }
    return true;
}
function isNextCharacterInside(element, direction, root, flags) {
    return !isLengthRangeMinZero(element, flags) || isNextCharacterInsideAfter(element, direction, root, flags);
}
/**
 * Returns whether the next character consumed after `afterThis` is entirely determined by the elements of the given
 * `root` alternative.
 *
 * This assumes that `afterThis` is a descendant of `root`.
 */
function isNextCharacterInsideAfter(afterThis, direction, root, flags) {
    const parent = afterThis.parent;
    if (parent.type === "CharacterClass" ||
        parent.type === "CharacterClassRange" ||
        parent.type === "ClassIntersection" ||
        parent.type === "ClassSubtraction" ||
        parent.type === "ExpressionCharacterClass" ||
        parent.type === "StringAlternative") {
        throw new Error("Expected an element outside a character class.");
    }
    if (parent.type === "Quantifier") {
        return isNextCharacterInsideAfter(parent, direction, root, flags);
    }
    const inc = direction === "ltr" ? +1 : -1;
    const start = parent.elements.indexOf(afterThis);
    for (let i = start + inc; i >= 0 && i < parent.elements.length; i += inc) {
        const e = parent.elements[i];
        if (!isLengthRangeMinZero(e, flags)) {
            return true;
        }
    }
    if (parent === root) {
        // since we reached the root, we couldn't find something is consumed a character
        return false;
    }
    const grandparent = parent.parent;
    if (grandparent.type === "Pattern") {
        throw new Error("Expected the given element to be a descendant of the root alternative.");
    }
    if (grandparent.type === "Assertion") {
        // honestly, this doesn't make sense, so let's just throw an error
        throw new Error();
    }
    return isNextCharacterInsideAfter(grandparent, direction, root, flags);
}
